import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import Nav from "./Nav";
import Swal from "sweetalert2";
import moment from "moment";
import Spinner from "./Spinner";
import Sidebar from "./Sidebar";
import { useNavContext } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import { orgCollection, orgDocIn } from "../utils/firePaths";
import Breadcrumb from "./Breadcrumb";
import ContactDetailModal from "./ContactDetailModal";
import ContactFormModal from "./ContactFormModal";
import TableToolbar from "./TableToolbar";
import DataTable from "./DataTable";
import TablePagination from "./TablePagination";

moment.locale("es");

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: "650px",
    borderRadius: "15px",
    padding: "0",
    border: "none",
    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
    overflow: "hidden",
    fontFamily: "'Inter', sans-serif",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    zIndex: 1009,
  },
};

const viewModalStyles = {
  ...customStyles,
  content: {
    ...customStyles.content,
    maxWidth: "850px",
  },
};

const ListaTodos = () => {
  const { user, orgId } = useAuth();
  const { proximasAVencer, proximosEventos } = useNavContext();
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [formData, setFormData] = useState({
    tipo: "Cliente",
    empresa: "",
    rfc: "",
    regimenFiscal: "",
    moneda: "",
    telefono: "",
    correo: "",
    domicilio: "",
    numeroExt: "",
    numeroInt: "",
    colonia: "",
    codigoPostal: "",
    ciudad: "",
    numeroRegistro: "",
    contactoPersonal: "",
  });
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [editingProveedorId, setEditingProveedorId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filterTipo, setFilterTipo] = useState("Todos");
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [viewModalIsOpen, setViewModalIsOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const guardado = localStorage.getItem("columnConfig_todos");
    const base = guardado
      ? JSON.parse(guardado)
      : {
          empresa: true,
          tipo: true,
          correo: true,
          telefono: true,
          fechaRegistro: false,
        };

    // limpieza defensiva
    if (base.tipo === undefined) base.tipo = true;
    if (base.estado !== undefined) delete base.estado;

    localStorage.setItem("columnConfig_todos", JSON.stringify(base));
    return base;
  });
  const [sortConfig, setSortConfig] = useState({
    key: "empresa",
    direction: "asc",
  });

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => {
      const direction =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  }, []);

  useEffect(() => {
    localStorage.setItem("columnConfig_todos", JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedInColumnMenu = e.target.closest(
        ".dropdown-custom.column-config",
      );
      const clickedInColumnBtn = e.target.closest('[data-dd="columns"]');

      const clickedInExportMenu = e.target.closest(
        ".dropdown-custom.export-menu",
      );
      const clickedInExportBtn = e.target.closest('[data-dd="export"]');

      if (showColumnConfig && !clickedInColumnMenu && !clickedInColumnBtn) {
        setShowColumnConfig(false);
      }

      if (showExportDropdown && !clickedInExportMenu && !clickedInExportBtn) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, [showColumnConfig, showExportDropdown]);

  useEffect(() => {
    if (!user?.uid || !orgId) return;
    setIsLoading(true);
    const unsubC = onSnapshot(orgCollection(orgId, "clientes"), (snap) => {
      setClientes(
        snap.docs.map((d) => ({ id: d.id, tipo: "Cliente", ...d.data() })),
      );
      setIsLoading(false);
    });

    const unsubP = onSnapshot(orgCollection(orgId, "proveedores"), (snap) => {
      setProveedores(
        snap.docs.map((d) => ({ id: d.id, tipo: "Proveedor", ...d.data() })),
      );
      setIsLoading(false);
    });
    return () => {
      unsubC();
      unsubP();
    };
  }, [user?.uid, orgId]);

  const parseFecha = (fecha) => {
    if (!fecha) return null;
    if (fecha.seconds) return new Date(fecha.seconds * 1000);
    if (fecha.toDate) return fecha.toDate();
    const d = new Date(fecha);
    return isNaN(d.getTime()) ? null : d;
  };

  const resetFormData = useCallback(() => {
    setFormData({
      tipo: "Cliente",
      empresa: "",
      rfc: "",
      regimenFiscal: "",
      moneda: "",
      telefono: "",
      correo: "",
      domicilio: "",
      numeroExt: "",
      numeroInt: "",
      colonia: "",
      codigoPostal: "",
      ciudad: "",
      numeroRegistro: "",
      contactoPersonal: "",
    });
    setEditingClientId(null);
    setEditingProveedorId(null);
  }, []);

  const handleOpenModal = () => {
    resetFormData();
    setModalIsOpen(true);
  };

  const toggleRow = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const filteredContacts = useMemo(() => {
    const lower = searchQuery.toLowerCase();
    return [...clientes, ...proveedores].filter((c) => {
      const matchesSearch = [c.tipo, c.empresa, c.rfc, c.correo].some((f) =>
        f?.toLowerCase().includes(lower),
      );
      const matchesTipo = filterTipo === "Todos" || c.tipo === filterTipo;
      return matchesSearch && matchesTipo;
    });
  }, [clientes, proveedores, searchQuery, filterTipo]);

  const sortedContacts = useMemo(() => {
    const { key, direction } = sortConfig;

    return [...filteredContacts].sort((a, b) => {
      // fechaRegistro
      if (key === "fechaRegistro") {
        const fa = parseFecha(a.fechaRegistro);
        const fb = parseFecha(b.fechaRegistro);
        const A = fa ? fa.getTime() : 0;
        const B = fb ? fb.getTime() : 0;
        return direction === "asc" ? A - B : B - A;
      }

      // texto
      const A = (a[key] ?? "").toString().toLowerCase();
      const B = (b[key] ?? "").toString().toLowerCase();
      return direction === "asc" ? A.localeCompare(B) : B.localeCompare(A);
    });
  }, [filteredContacts, sortConfig]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedContacts.length / itemsPerPage),
  );
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const currentItems = useMemo(() => {
    return sortedContacts.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage,
    );
  }, [sortedContacts, currentPage, itemsPerPage]);

  const currentRows = useMemo(() => {
    return currentItems.map((i) => ({
      ...i,
      __rowId: `${i.tipo}:${i.id}`,
    }));
  }, [currentItems]);

  const toggleSelectAll = useCallback(() => {
    const ids = currentRows.map((r) => r.__rowId);
    const allSelected =
      ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    setSelectedIds((prev) =>
      allSelected
        ? prev.filter((x) => !ids.includes(x))
        : [...new Set([...prev, ...ids])],
    );
  }, [currentRows, selectedIds]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.empresa.trim()) {
      return Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "La Razón Social es obligatoria!",
      });
    }
    try {
      let colName = editingClientId
        ? "clientes"
        : editingProveedorId
          ? "proveedores"
          : formData.tipo === "Cliente"
            ? "clientes"
            : "proveedores";
      const colRef = orgCollection(orgId, colName);
      const datosAGuardar = {
        ...formData,
        tipo: colName === "clientes" ? "Cliente" : "Proveedor",
      };
      if (!editingClientId && !editingProveedorId)
        datosAGuardar.fechaRegistro = serverTimestamp();
      Object.keys(datosAGuardar).forEach(
        (key) => datosAGuardar[key] === undefined && delete datosAGuardar[key],
      );
      delete datosAGuardar.id;
      const id = editingClientId || editingProveedorId;
      if (id) {
        await updateDoc(orgDocIn(orgId, colName, id), datosAGuardar);
      } else {
        await addDoc(colRef, datosAGuardar);
      }
      setSelectedIds([]);
      setModalIsOpen(false);
      resetFormData();
      Swal.fire({
        icon: "success",
        title: "Cambios guardados",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteSelected = async () => {
    if (!user?.uid || !orgId || selectedIds.length === 0) return;
    const result = await Swal.fire({
      html: `
      <div class="custom-modal-container">
        <div class="custom-modal-header">
          <div class="custom-modal-icon-box">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" x2="12" y1="8" y2="12"></line><line x1="12" x2="12.01" y1="16" y2="16"></line></svg>
          </div>
          <h3 class="custom-modal-title">Eliminar Contactos</h3>
        </div>
        <p class="custom-modal-description">¿Estás seguro de que deseas eliminar ${selectedIds.length} contacto(s)?</p>
        <div class="custom-modal-actions">
          <button id="cancel-btn" class="btn-modal-cancel">Cancelar</button>
          <button id="confirm-btn" class="btn-modal-delete">Eliminar</button>
        </div>
      </div>
    `,
      showConfirmButton: false,
      showCancelButton: false,
      buttonsStyling: false,
      allowOutsideClick: false,
      customClass: {
        popup: "swal-popup-reset",
        htmlContainer: "swal-html-reset",
      },
      didOpen: () => {
        document
          .getElementById("cancel-btn")
          .addEventListener("click", () => Swal.close());
        document
          .getElementById("confirm-btn")
          .addEventListener("click", () => Swal.clickConfirm());
      },
    });

    if (!result.isConfirmed) return;

    try {
      await Promise.all(
        selectedIds.map(async (key) => {
          const [tipo, id] = key.split(":");
          const colName = tipo === "Cliente" ? "clientes" : "proveedores";
          await deleteDoc(orgDocIn(orgId, colName, id));
        }),
      );
      setSelectedIds([]);
      setViewModalIsOpen(false);
      setSelectedContact(null);
      Swal.fire({
        icon: "success",
        title: "Completado",
        text: "Eliminados correctamente.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditSelected = () => {
    if (selectedIds.length !== 1) return;
    const [tipo, id] = selectedIds[0].split(":");
    const contacto = [...clientes, ...proveedores].find(
      (c) => c.id === id && c.tipo === tipo,
    );
    if (!contacto) return;
    setFormData({ ...contacto });
    if (tipo === "Cliente") setEditingClientId(id);
    else setEditingProveedorId(id);
    setModalIsOpen(true);
  };

  const handleAutoAsignar = () => {
    const isCliente = formData.tipo === "Cliente";
    const prefijo = isCliente ? "C" : "P";
    const lista = isCliente ? clientes : proveedores;
    const max = Math.max(
      0,
      ...lista
        .map((i) => Number(i.numeroRegistro?.split("-")[1]))
        .filter(Boolean),
    );
    setFormData((p) => ({
      ...p,
      numeroRegistro: `${prefijo}-${String(max + 1).padStart(4, "0")}`,
    }));
  };

  const columnOptionsTodos = useMemo(
    () => [
      { key: "empresa", label: "Razón Social" },
      { key: "tipo", label: "Tipo" },
      { key: "correo", label: "Correo" },
      { key: "telefono", label: "Teléfono" },
      { key: "fechaRegistro", label: "Fecha de Registro" },
    ],
    [],
  );

  const columnsTodos = useMemo(() => {
    return [
      {
        id: "empresa",
        label: "Razón Social",
        visible: visibleColumns.empresa,
        sortKey: "empresa",
        cellClassName: "table-td col-left",
        render: (c) => c.empresa || "---",
      },
      {
        id: "tipo",
        label: "Tipo",
        visible: visibleColumns.tipo,
        sortKey: "tipo",
        cellClassName: "table-td col-center",
        render: (c) => (
          <span
            className={`type-badge ${
              c.tipo === "Cliente" ? "badge-cliente" : "badge-proveedor"
            }`}
          >
            {c.tipo}
          </span>
        ),
      },
      {
        id: "correo",
        label: "Correo",
        visible: visibleColumns.correo,
        sortKey: "correo",
        cellClassName: "table-td col-center",
        render: (c) => c.correo || "---",
      },
      {
        id: "telefono",
        label: "Teléfono",
        visible: visibleColumns.telefono,
        sortKey: "telefono",
        cellClassName: "table-td col-center",
        render: (c) => c.telefono || "---",
      },
      {
        id: "fechaRegistro",
        label: "Fecha de Registro",
        visible: visibleColumns.fechaRegistro,
        sortKey: "fechaRegistro",
        cellClassName: "table-td col-center",
        render: (c) => {
          const f = parseFecha(c.fechaRegistro);
          return f ? moment(f).format("DD/MM/YYYY") : "N/A";
        },
      },
    ];
  }, [visibleColumns]);

  return (
    <>
      <div className="no-sticky">
        <Nav
          proximasAVencer={proximasAVencer}
          proximosEventos={proximosEventos}
        />
        <Sidebar />
        <div className="main-content-wrapper">
          <div style={{ marginBottom: "25px" }}>
            <Breadcrumb />
            <h1 className="page-title">Todos los Contactos</h1>
          </div>

          <TableToolbar
            // búsqueda
            searchTerm={searchQuery}
            setSearchTerm={setSearchQuery}
            searchPlaceholder="Buscar contactos..."
            // filtros (panel externo)
            showFilterDropdown={showFilters}
            setShowFilterDropdown={setShowFilters}
            filterStatus={filterTipo}
            setFilterStatus={setFilterTipo}
            filterOptions={["Todos", "Cliente", "Proveedor"]}
            // columnas
            showColumnConfig={showColumnConfig}
            setShowColumnConfig={setShowColumnConfig}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
            columnOptions={columnOptionsTodos}
            // export
            showExportDropdown={showExportDropdown}
            setShowExportDropdown={setShowExportDropdown}
            fileInputRef={null} // si luego implementas import, aquí va tu ref real
            onExport={() => {
              // aquí va tu export real (xlsx/pdf)
              setShowExportDropdown(false);
            }}
            // nuevo
            onAdd={handleOpenModal}
            addButtonTooltip="Nuevo"
          />

          {selectedIds.length > 0 && (
            <div className="selection-bar">
              <span>
                {selectedIds.length === 1
                  ? "1 seleccionado"
                  : `${selectedIds.length} seleccionado(s)`}
              </span>

              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="btn-selection btn-edit"
                  style={{ opacity: selectedIds.length > 1 ? 0.5 : 1 }}
                  onClick={handleEditSelected}
                  disabled={selectedIds.length > 1}
                >
                  <i className="bi bi-pencil-square" /> <span>Editar</span>
                </button>

                <button
                  className="btn-selection btn-delete"
                  onClick={handleDeleteSelected}
                >
                  <i className="bi bi-trash" /> <span>Eliminar</span>
                </button>
              </div>
            </div>
          )}

          <DataTable
            loading={isLoading}
            loadingFallback={
              <div style={{ padding: "40px" }}>
                <Spinner />
              </div>
            }
            rows={currentRows}
            rowKey="__rowId"
            columns={columnsTodos}
            selectedIds={selectedIds}
            onToggleAll={toggleSelectAll}
            onToggleRow={toggleRow}
            sortConfig={sortConfig}
            onSort={handleSort}
            onRowClick={(row) => {
              setSelectedContact(row);
              setViewModalIsOpen(true);
            }}
            emptyTitle="Aún no hay contactos."
            emptySubtitle="Crea un cliente o proveedor para comenzar."
            emptyIcon={
              <img
                src="/img/no-data.svg"
                alt="Sin datos"
                style={{ width: 220, maxWidth: "70%", opacity: 0.95 }}
              />
            }
            footer={
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(p) => setCurrentPage(p)}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={(n) => {
                  setItemsPerPage(n);
                  setCurrentPage(1);
                }}
                itemsPerPageOptions={[5, 10, 20]}
                selectedCount={selectedIds.length}
                selectedLabelSingular="contacto"
                selectedLabelPlural="contactos"
                itemsLabel="Mostrando:"
              />
            }
          />
        </div>
      </div>

      <ContactFormModal
        isOpen={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        handleAutoAsignar={handleAutoAsignar}
        isEditing={!!(editingClientId || editingProveedorId)}
      />
      {viewModalIsOpen && (
        <ContactDetailModal
          isOpen={viewModalIsOpen}
          onClose={() => setViewModalIsOpen(false)}
          contact={selectedContact}
          modalStyles={viewModalStyles}
        />
      )}
    </>
  );
};

export default ListaTodos;
